import cloneDeep from 'lodash/cloneDeep'
import differenceBy from 'lodash/differenceBy'
import get from 'lodash/get'
import uniq from 'lodash/uniq'
import { createReducer } from 'redux-create-reducer'
import type {
  Selection,
  SelectionId,
  SelectionItem,
  SelectionRange,
  SelectionType,
  SelectionHistory,
} from 'types/selection'
import {
  ADD_EMPTY_SELECTION,
  REMOVE_DATA_TABLE_SELECTION,
  REMOVE_SELECTION,
  SELECTION_CHANGE,
  SELECTION_RESET,
  TRACK_MERGE_SUCCESS,
  UPDATE_SELECTION_ITEMS,
} from '@disco/constants/ActionTypes'
import {
  INDIVIDUAL_ITEM_SELECTION,
  RANGE_SELECTION,
} from '@disco/constants/SelectionTypes'
const initialState = {}

function getSortedRange(range: SelectionRange) {
  return uniq(range).sort((a, b) => a - b)
}

function changeSelection(
  selection: State,
  {
    selectionId,
    selectionRange,
    selectionType,
  }: {
    selectionId: SelectionId
    selectionRange: SelectionRange
    selectionType: SelectionType
  },
) {
  let indexes: number[] = []
  const currentSelection =
    selectionId != null ? selection[selectionId] : undefined
  const selectedIndexes = get(currentSelection, 'indexes', null)
  const selectionHistory = get(currentSelection, 'history', [])
  let selectionMarker = get(currentSelection, 'selectionStartMarker', 0)

  if (selectedIndexes) {
    if (selectionType === INDIVIDUAL_ITEM_SELECTION) {
      // selectionRange always has at least one element for INDIVIDUAL_ITEM_SELECTION
      const selectionItemIndex = selectedIndexes.indexOf(selectionRange[0]!)

      if (selectionItemIndex > -1) {
        // if selected item already exists in array, remove it
        indexes = [
          ...selectedIndexes.slice(0, selectionItemIndex),
          ...selectedIndexes.slice(selectionItemIndex + 1),
        ]

        if (selectionItemIndex < selectedIndexes.length - 1) {
          // index within bounds: verified by condition above
          selectionMarker = selectedIndexes[selectionItemIndex + 1]!
        } else if (selectionItemIndex > 0) {
          // index within bounds: verified by condition above
          selectionMarker = selectedIndexes[selectionItemIndex - 1]!
        } else {
          selectionMarker = 0
        }
      } else {
        // if not, just use regular range scenario
        indexes = getSortedRange([...selectedIndexes, ...selectionRange])
        // individual selection sets selection marker for further range selections
        // selectionRange always has at least one element
        selectionMarker = selectionRange[0]!
      }
    } else {
      const previousSelection =
        selectionHistory.length > 0
          ? selectionHistory[selectionHistory.length - 1]
          : null

      if (previousSelection && previousSelection.type === RANGE_SELECTION) {
        // user refines range selection, remove previous selected range
        // and add updated one
        if (selectionHistory.length > 1) {
          indexes = getSortedRange([
            ...selectedIndexes.filter(index => {
              return previousSelection.range.indexOf(index) === -1
            }),
            ...selectionRange,
          ])
        } else {
          indexes = getSortedRange(selectionRange)
        }
      } else {
        // for range selection just create array with unique indexes
        indexes = getSortedRange([...selectedIndexes, ...selectionRange])
      }
    }
  } else {
    indexes = getSortedRange(selectionRange)

    if (selectionType === INDIVIDUAL_ITEM_SELECTION) {
      // selectionRange has at least one element
      selectionMarker = selectionRange[0]!
    } else if (selectionType === RANGE_SELECTION) {
      // selectionRange is non-empty when called for RANGE_SELECTION
      selectionMarker = selectionRange[selectionRange.length - 1]!
    }
  }

  return {
    indexes,
    selectionStartMarker: selectionMarker,
    history: [
      ...selectionHistory,
      {
        type: selectionType,
        range: selectionRange,
      },
    ],
  }
}

const countNumbersBelowItem = (index: number, indexesList: number[]) =>
  indexesList.filter(listItem => listItem <= index).length

const resetSelection = state => {
  const newState = cloneDeep(state)

  for (const selectionId in newState) {
    const selection = newState[selectionId]
    selection.indexes = []
    delete selection.selectionStartMarker
  }

  return newState
}

// When selecting within a nested sub-pane, don't clear the parent pane selection
// (and vice versa). Only reset top-level panes when changing top-level selection.
const resetSelectionExcludingNested = (state, isNestedAction: boolean) => {
  if (isNestedAction) return state
  const newState = cloneDeep(state)
  for (const selectionId in newState) {
    const selection = newState[selectionId]
    if (!selection.isNested) {
      selection.indexes = []
      delete selection.selectionStartMarker
    }
  }
  return newState
}

const adjustItemsByIds = (
  newSelection: Selection,
  currentSelection: Selection,
): {
  indexes: number[]
  history: SelectionHistory[]
} => {
  const diff: SelectionItem[] = differenceBy(
    currentSelection.selectionItems,
    newSelection.selectionItems,
    'id',
  )
  if (diff.length === 0)
    return {
      indexes: newSelection.indexes,
      history: [],
    }
  const removedIndexes = currentSelection.selectionItems
    .map((item, index) => {
      const itemHasRemoved = diff.find(({ id }) => item.id === id)

      if (itemHasRemoved) {
        return index
      }
    })
    .filter(item => typeof item === 'number')
  // get survived after removal indexes
  const leftIndexes = newSelection.indexes.filter(
    index => !removedIndexes.includes(index),
  )
  const result = {
    // history is useless now
    history: [],
    // adjust survived indexes
    indexes: leftIndexes.map(
      selectedIndex =>
        selectedIndex - countNumbersBelowItem(selectedIndex, removedIndexes),
    ), // don't adjust selection start marker
  }
  return result
}

export type State = Record<string, Selection>
export const selectionReducer = createReducer<State, any>(initialState, {
  [TRACK_MERGE_SUCCESS]() {
    return initialState
  },

  [ADD_EMPTY_SELECTION](state, action) {
    return {
      ...state,
      [action.selectionId]: {
        indexes: [],
        selectionItems: action.selectionItems,
        paneIndex: action.paneIndex,
        isNested: action.isNested,
      },
    }
  },

  [REMOVE_SELECTION](state, action) {
    const newState = cloneDeep(state)

    if (newState?.[action.selectionId]) {
      delete newState[action.selectionId]
    }

    return newState
  },

  [UPDATE_SELECTION_ITEMS](state, action) {
    const newState = cloneDeep(state)
    const newSelection = newState?.[action.selectionId]
    const currentSelection = state?.[action.selectionId]

    if (newSelection && currentSelection) {
      newSelection.selectionItems = action.selectionItems
      newSelection.paneIndex = action.paneIndex
      const adjustItems = adjustItemsByIds(newSelection, currentSelection)

      if (adjustItems.history) {
        newSelection.history = adjustItems.history
      }

      newSelection.indexes = adjustItems.indexes
    }

    return newState
  },

  [SELECTION_CHANGE](state, action) {
    const isNestedAction = Boolean(state[action.selectionId]?.isNested)
    const resetedState = resetSelectionExcludingNested(state, isNestedAction)
    return {
      ...resetedState,
      [action.selectionId]: {
        ...state[action.selectionId],
        ...changeSelection(state, action),
      },
    }
  },

  [SELECTION_RESET](state) {
    return resetSelection(state)
  },

  [REMOVE_DATA_TABLE_SELECTION](state, action) {
    const selection = state[action.selectionId]
    if (!selection) return state
    const { selectionStartMarker: _omit, ...rest } = selection
    return {
      ...state,
      [action.selectionId]: {
        ...rest,
        indexes: [],
        history: [],
      },
    }
  },
})
